import React, { useCallback, useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ContainedButton, AddIcon } from 'secondstep-components'
import { toKebabCase } from 'utils/stringHelpers'
import { TextButton, StyledDropdownMenu } from './component.styles'
import {
  INITIAL_COURSE,
  SMALL_GROUP_LABELS,
} from 'experienceComponents/CreateInstanceShared/constants.js'
import {
  getLessonsLink,
  checkLimitReached,
  getInitialSelectedSchool,
} from './data'
import { smallGroupFilterConfig } from './smallGroupConfig'
import ModalRouter from './modalRouter'
import { useCreateSmallGroupHandlers } from './stateManagement'
import { useSideEffects } from './useSideEffects'

const CreateSmallGroup = props => {
  // Initialize state
  const [state, setState] = useState(() => {
    try {
      return {
        isFormValid: false,
        inputErrors: {
          className: {
            errors: null,
          },
          selectedGrade: '',
        },
        courseInstanceName: props.courseInstanceName || '',
        sitesWithGrades: [],
        modalContent: null,
        modalFlow: null,
        selectedCourse: INITIAL_COURSE,
        selectedSchool: getInitialSelectedSchool(props, smallGroupFilterConfig),
        submitAttempts: 0,
      }
    } catch (error) {
      console.error('Error initializing state:', error)
      return {
        isFormValid: false,
        inputErrors: {
          className: { errors: null },
          selectedGrade: '',
        },
        courseInstanceName: props.courseInstanceName || '',
        sitesWithGrades: [],
        modalContent: null,
        modalFlow: null,
        selectedCourse: INITIAL_COURSE,
        selectedSchool: null,
        submitAttempts: 0,
      }
    }
  })

  // TODO: refactor this to not need so many side effects. This is just the initial version. Need to rethink ordering of events to avoid so many updates
  // Ref to track side effects
  const sideEffectsRef = useRef({})

  // Create state updater that handles side effects
  const updateState = useCallback((newState, sideEffects = {}) => {
    setState(newState)
    sideEffectsRef.current = sideEffects
  }, [])

  // Get all handlers from the custom hook
  const {
    resetStateCallback,
    handleCreateCallback,
    handleSubmitCallback,
    handleSaveEditCallback,
    handleDeleteCallback,
    handleCancelCallback,
    handleViewClassCallback,
    handleClassNameChangeCallback,
    handleGradeChangeCallback,
    handleSiteChangeCallback,
    goBackOneModalCallback,
    validateFormCallback,
    handleHideCallback,
    handleHideSubmitCallback,
    handleDeleteSubmitCallback,
    handleEditSubmitCallback,
  } = useCreateSmallGroupHandlers(state, props, updateState)

  // Allow parent to programmatically open the Create modal without clicking a button
  useEffect(() => {
    if (props.autoOpen) {
      handleCreateCallback()
    }
  }, [props.autoOpen, handleCreateCallback])

  useSideEffects(
    sideEffectsRef,
    state,
    props,
    handleSiteChangeCallback,
    handleGradeChangeCallback,
    validateFormCallback,
  )

  // Destructure state for easier access
  const {
    courseInstanceName,
    error,
    inputErrors,
    isFormValid,
    sitesWithGrades,
    modalContent,
    modalFlow,
    selectedCourse,
    selectedGrade,
    selectedSchool,
  } = state

  // Destructure props for easier access
  const {
    className,
    course,
    edit,
    emptyClasses,
    forwardedRef,
    isClassCardMenu,
    limitReached: maxClasses,
  } = props

  const { instance } = course || {}

  return (
    <div className={className}>
      {!props.hideLauncher &&
        !edit &&
        !checkLimitReached(props.pathName, props.lms, maxClasses) &&
        !emptyClasses && (
          <TextButton
            dataTestId={'button-create-small-group'}
            icon={<AddIcon />}
            label={SMALL_GROUP_LABELS.CREATE_BUTTON}
            onClick={handleCreateCallback}
            ref={forwardedRef}
          />
        )}
      {!props.hideLauncher && emptyClasses && (
        <ContainedButton
          dataTestId={'button-create-small-group'}
          onClick={handleCreateCallback}
          ref={forwardedRef}
        >
          {SMALL_GROUP_LABELS.CREATE_BUTTON}
        </ContainedButton>
      )}
      {edit && (
        <StyledDropdownMenu
          courseInstanceName={courseInstanceName}
          dataTestId={`dropdown-menu-button-${toKebabCase(courseInstanceName)}`}
          handleDeleteSubmit={handleDeleteSubmitCallback}
          handleEditSubmit={handleEditSubmitCallback}
          handleHideSubmit={handleHideSubmitCallback}
          instance={instance}
          isClassCardMenu={isClassCardMenu}
          ref={forwardedRef}
        />
      )}
      <ModalRouter
        contentfulCourses={props.contentfulCourses}
        courseInstanceName={courseInstanceName}
        currentSite={props.currentSite}
        error={error}
        getLessonsLink={() => getLessonsLink(props.pathName)}
        goBackOneModal={goBackOneModalCallback}
        handleCancel={handleCancelCallback}
        handleClassNameChange={handleClassNameChangeCallback}
        handleCreate={handleCreateCallback}
        handleDelete={handleDeleteCallback}
        handleGradeChange={handleGradeChangeCallback}
        handleHideSmallGroup={handleHideCallback}
        handleSaveEdit={handleSaveEditCallback}
        handleSiteChange={handleSiteChangeCallback}
        handleSubmit={handleSubmitCallback}
        handleViewClass={handleViewClassCallback}
        inputErrors={inputErrors}
        isClassCardMenu={isClassCardMenu}
        isFormValid={isFormValid}
        limitReached={checkLimitReached(props.pathName, props.lms, maxClasses)}
        lms={props.lms}
        modalContent={modalContent}
        modalFlow={modalFlow}
        navigationEvent={props.navigationEvent}
        redirectToMyClasses={props.redirectToMyClasses}
        resetState={resetStateCallback}
        selectedCourse={selectedCourse}
        selectedGrade={selectedGrade}
        selectedSchool={selectedSchool}
        sitesWithGrades={sitesWithGrades}
      />
    </div>
  )
}

CreateSmallGroup.propTypes = {
  autoOpen: PropTypes.bool,
  className: PropTypes.string,
  classesUrl: PropTypes.string,
  contentfulCourses: PropTypes.array,
  course: PropTypes.object,
  courseInstanceName: PropTypes.string,
  createCourseInstanceV2ProgramFlex: PropTypes.func.isRequired,
  currentSite: PropTypes.shape({
    isSetup: PropTypes.bool,
    siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    siteName: PropTypes.string,
  }),
  deleteCourseInstance: PropTypes.func.isRequired,
  deletingCourseCreatedStatus: PropTypes.func.isRequired,
  edit: PropTypes.bool,
  emptyClasses: PropTypes.bool,
  fetchInstructorData: PropTypes.func.isRequired,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  hideLauncher: PropTypes.bool,
  isClassCardMenu: PropTypes.bool,
  isCreated: PropTypes.bool,
  license: PropTypes.object,
  limitReached: PropTypes.bool,
  lms: PropTypes.object.isRequired,
  navigationEvent: PropTypes.func,
  pathName: PropTypes.string,
  redirectToMyClasses: PropTypes.func,
  setFocus: PropTypes.func,
  updateCourseInstance: PropTypes.func.isRequired,
  userAccess: PropTypes.object,
  userContext: PropTypes.object,
  userProfile: PropTypes.object,
}

CreateSmallGroup.defaultProps = {
  autoOpen: false,
  hideLauncher: false,
  course: {},
  className: '',
  edit: false,
  emptyClasses: false,
  isClassCardMenu: false,
  limitReached: false,
}

export default CreateSmallGroup
